// src/lib/queuedWrite.js
//
// The universal offline-first write helper. Every mutation in the app should
// go through here instead of calling supabase directly.
//
// Online path:
//   Tries Supabase directly with a 20s timeout. On success, returns the
//   payload. The mutation queue is never touched — no overhead for the common
//   connected case.
//
// Offline / timeout path:
//   Writes to the PGlite _mutation_queue immediately (non-blocking, survives
//   page reloads). Throws a { queued: true } error so callers can show
//   appropriate UI ("Saved — will sync when reconnected") instead of a failure.
//   The connectionWatchdog drains the queue to Supabase automatically on
//   reconnect (≤30s).
//
// Usage:
//   import { queuedWrite } from '../lib/queuedWrite'
//
//   // Insert (client generates UUID — safe to retry as upsert)
//   const row = await queuedWrite('tasks', 'insert', { title, status, ... })
//
//   // Partial update (only changed fields — sent as UPDATE ... WHERE id = rowId)
//   await queuedWrite('tasks', 'update', { status: 'done', updated_at: now }, taskId)
//
//   // Upsert (full row, caller supplies id)
//   await queuedWrite('tasks', 'upsert', { id, title, ... })
//
//   // Delete
//   await queuedWrite('tasks', 'delete', null, taskId)
//
// Catching the queued signal:
//   try {
//     await queuedWrite(...)
//     toast.success('Saved')
//   } catch (e) {
//     if (e.queued) toast.warning(e.message)
//     else toast.error(e.message)
//   }

import { supabase, isEmbed, rebridgeSession } from './supabase'
import { isOnline as isSupabaseOnline } from './connectionWatchdog'
import { enqueueMutation } from './localDb'

const TIMEOUT_MS = 20_000

function _withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_MS)
    ),
  ])
}

// Errors we should NOT queue — validation / auth failures won't be fixed by
// retrying later. Let them bubble up immediately as real errors.
function _isPermanentError(err) {
  const msg = (err?.message || '').toLowerCase()
  if (msg.includes('violates') || msg.includes('duplicate') || msg.includes('foreign key')) return true
  if (msg.includes('permission') || msg.includes('not authorized') || msg.includes('jwt')) return true
  if (msg.includes('invalid input') || msg.includes('does not exist')) return true
  return false
}

// Auth failures (stale/expired token) — in the Gmail embed these are recoverable
// by re-bridging a fresh session from the parent, so we retry once before
// treating them as permanent.
function _isAuthError(err) {
  const msg = (err?.message || '').toLowerCase()
  return msg.includes('jwt') || msg.includes('not authorized') || msg.includes('permission denied') ||
    msg.includes('401') || err?.code === 'PGRST301' || err?.code === '42501'
}

// Is this a connectivity error we should queue instead of surface?
function _isNetworkError(err) {
  const msg = (err?.message || '').toLowerCase()
  return (
    msg.includes('timed out') ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network error') ||
    msg.includes('load failed') ||
    msg.includes('unreachable')
  )
}

async function _enqueue(tableName, operation, payload, rowId) {
  try {
    await enqueueMutation({ tableName, operation, rowId, payload })
  } catch (qErr) {
    // PGlite unavailable — still throw the queued signal so callers know
    // the live write failed. The user will need to retry when reconnected.
    console.warn('[queuedWrite] PGlite queue unavailable:', qErr?.message)
  }
  const err = new Error('Saved locally — will sync when reconnected')
  err.queued = true
  err.payload = payload
  throw err
}

/**
 * @param {string}  tableName  Supabase table name
 * @param {string}  operation  'insert' | 'update' | 'upsert' | 'delete'
 * @param {object|null} payload  Row data. For delete, pass null.
 * @param {string|null} rowId   Required for update/delete. For insert, auto-generated if payload has no id.
 * @returns {object} The payload (with id populated for inserts)
 */
export async function queuedWrite(tableName, operation, payload = null, rowId = null, _authRetried = false) {
  // Normalise id
  const id = rowId || payload?.id || (operation === 'insert' ? crypto.randomUUID() : null)
  const norm = payload ? { ...payload, id: id ?? payload.id } : null

  // Online — try Supabase directly
  if (isSupabaseOnline()) {
    try {
      if (operation === 'delete') {
        const { error } = await _withTimeout(
          supabase.from(tableName).delete().eq('id', id)
        )
        if (error && !_isPermanentError(error)) throw error
        if (error) throw error
      } else if (operation === 'update') {
        // Strip `id` from the update body — PostgREST rejects (400) when the
        // primary key is included in the PATCH body alongside a ?id=eq.X filter.
        const { id: _stripId, ...updateFields } = norm
        const { error } = await _withTimeout(
          supabase.from(tableName).update(updateFields).eq('id', id)
        )
        if (error) {
          console.error(`[queuedWrite] update error on ${tableName} id=${id}:`, error)
          throw error
        }
      } else {
        // insert or upsert — use upsert so retries are idempotent
        const { error } = await _withTimeout(
          supabase.from(tableName).upsert(norm)
        )
        if (error) throw error
      }
      return norm
    } catch (err) {
      // Gmail embed: a stale/expired bridged token surfaces as a JWT/permission
      // error. Ask the parent for a fresh session and retry ONCE — so in-dock
      // writes (link entity, etc.) don't fail just because the token aged out.
      if (isEmbed && !_authRetried && _isAuthError(err)) {
        const ok = await rebridgeSession()
        if (ok) return await queuedWrite(tableName, operation, payload, id, true)
      }
      if (_isPermanentError(err)) throw err   // surface validation errors immediately
      if (!_isNetworkError(err)) throw err    // surface unexpected errors too
      // Network/timeout — fall through to queue
      console.warn(`[queuedWrite] ${tableName}/${operation} network error, queuing:`, err.message)
    }
  }

  // Offline / network failure — queue it
  const queueOperation = operation === 'insert' ? 'upsert' : operation
  await _enqueue(tableName, queueOperation, norm, id)
  // _enqueue always throws — this line is unreachable but satisfies linters
  return norm
}

/**
 * Turn a failed write into something the user can act on. An RLS denial (42501)
 * on a table every signed-in teammate can write means one thing only: we aren't
 * signed in. "Failed to create company" sends people hunting for a bug in the
 * form; "Signed out" tells them what to fix.
 */
export function writeErrorMessage(err, fallback = 'Save failed') {
  if (err?.queued) return err.message
  const msg = String(err?.message || '')
  if (
    err?.code === '42501' || err?.code === 'PGRST301' ||
    /row-level security|jwt|not authorized|permission denied/i.test(msg)
  ) {
    return isEmbed ? 'Signed out — reconnect to save' : 'Session expired — reload the page'
  }
  return fallback
}

/**
 * Optimistic insert — like queuedWrite('insert') but always returns the row,
 * even when offline. Use in onCreate / picker callbacks that need a row ID
 * back immediately for local state. The actual DB write is queued if offline.
 */
export async function queuedInsert(tableName, payload) {
  try {
    return await queuedWrite(tableName, 'insert', payload)
  } catch (e) {
    if (e.queued) return e.payload   // optimistic: caller gets the row with client UUID
    throw e
  }
}
